<template>
  <FormSection
    id="form-general-settings"
    v-if="canSetRecurring && featureEnabled && isAuthorized"
  >
    <Row
      type="flex"
      align="baseline"
      justify="space-between"
      class="recurring-header"
    >
      <Column :md="10">
        <FormGroup
          class="recurring_enabled"
          prop="recurring_enabled"
          :no-margin="true"
        >
          <ToggleSwitch
            class="state-switch no-wrap"
            v-model="recurringEnabled"
            data-test="recurring_enabled"
            :disabled="recurring_draft || !userHasPremiumPlan"
            id="recurring_enabled"
            @click.native.stop="showUpgradeDialog"
          >
            <template slot="active-content">
              <div class="recurring-label">
                {{ $t('form.invoiceForm.fields.recurring.enabled.label') }}
                <Tooltip
                  effect="blue"
                  placement="right"
                  :open-delay="100"
                  :content="$t(`form.invoiceForm.fields.recurring.inputHelp`)"
                >
                  <i
                    class="icon icon-question-circle"
                    style="margin-left: 5px; cursor: help;"
                  />
                </Tooltip>
              </div>
            </template>
          </ToggleSwitch>
        </FormGroup>
      </Column>
      <Column :md="14">
        <div
          v-if="recurringEnabled"
          class="recurring-dates"
        >
          <div
            data-test="recurring_current_invoice_date"
            class="recurring-date"
          >
            <span class="text--md text--secondary-color">
              {{ $t('form.invoiceForm.fields.recurring.currentInvoiceDate') }}
            </span>
            <span class="text--lg text--semibold">{{ issued_on | formatDate('dd. MM. yyyy') }}</span>
            <Anchor
              @click="handleChangeDateAnchorClick"
              ref="anchor"
            >
              <span class="text--semibold">{{ $t('form.invoiceForm.fields.recurring.changeCurrentInvoiceDate') }}</span>
            </Anchor>
            <DatePicker
              v-model="issued_on"
              ref="datepicker"
              value-format="timestamp"
              :append-to-body="true"
              :format="dateFormats.elementUi"
              class="datepicker-helper"
              @change="handleDateSelected"
            />
          </div>
          <div
            data-test="recurring_next_invoice_date"
            class="recurring-date"
          >
            <span class="text--md text--secondary-color">{{ $t('form.invoiceForm.fields.recurring.nextInvoiceDate') }}</span>
            <span class="text--lg text--semibold">{{ nextInvoiceDate | formatDate('dd. MM. yyyy') }}</span>
          </div>
        </div>
      </Column>
    </Row>

    <Row
      type="flex"
      align="top"
      v-if="recurringEnabled"
      class="inputs-wrapper"
    >
      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          prop="recurring_frequency"
          :label="$t('form.invoiceForm.fields.recurring.frequency.label')"
          vertical
        >
          <Select
            class="dropdown-steady"
            name="language"
            v-model="recurring_frequency"
            autocomplete="new-password"
            data-test="recurring_frequency"
            filterable
            :popper-append-to-body="false"
          >
            <Option
              v-for="option in frequencyOptions"
              :key="option.key"
              :label="option.value"
              :value="option.key"
            />
          </Select>
        </FormGroup>
      </Column>
      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          prop="recurring_end_options"
          :label="$t('form.invoiceForm.fields.recurring.endOptions.label')"
          vertical
        >
          <Select
            class="dropdown-steady"
            name="language"
            v-model="recurring_end_options"
            autocomplete="new-password"
            data-test="recurring_end_options"
            filterable
            :popper-append-to-body="false"
          >
            <Option
              v-for="option in whenEndOptions"
              :key="option.key"
              :label="option.value"
              :value="option.key"
            />
          </Select>
        </FormGroup>
      </Column>

      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          vertical
          prop="recurring_ends_on"
          :label="$t('form.invoiceForm.fields.recurring.endAfterDate.label')"
          v-if="showDateInput"
        >
          <DatePicker
            v-model="recurring_ends_on"
            data-test="recurring_ends_on"
            :clearable="false"
            :format="dateFormats.elementUi"
            :picker-options="pickerOptions"
            :default-value="firstAvailableDate"
          />
        </FormGroup>
        <FormGroup
          vertical
          prop="recurring_ends_after_count"
          :label="$t('form.invoiceForm.fields.recurring.endAfterCount.label')"
          v-if="showCountInput"
        >
          <TextInput
            name="recurring_ends_after_count"
            data-test="recurring_ends_after_count"
            type="number"
            v-model.number="recurring_ends_after_count"
            id="recurring_ends_after_count"
          />
        </FormGroup>
      </Column>
    </Row>

    <Row
      type="flex"
      align="top"
      line
      class="recurring-checkboxes"
      v-if="recurringEnabled"
    >
      <Column
        :xs="24"
        :sm="12"
        :md="8"
        v-if="canSetRecurringLastDayInMonth"
      >
        <FormGroup
          class="iban-form-group"
          prop="recurring_issue_last_day"
        >
          <Checkbox
            v-model="recurring_issue_last_day"
            data-test="recurring_issue_last_day"
            id="recurring_issue_last_day"
          >
            {{ $t('form.invoiceForm.fields.recurring.issueLastDay.label') }}
          </Checkbox>
        </FormGroup>
      </Column>
      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          class="iban-form-group"
          prop="recurring_auto_submit"
        >
          <Checkbox
            v-model="recurring_auto_submit"
            data-test="recurring_auto_submit"
            id="recurring_auto_submit"
          >
            {{ $t('form.invoiceForm.fields.recurring.autoSubmit.label') }}
          </Checkbox>
        </FormGroup>
      </Column>

      <Column
        :xs="24"
        :sm="12"
        :md="8"
        v-if="canSetCopyTomyEmail"
      >
        <FormGroup
          class="iban-form-group"
          prop="recurring_submit_copy"
        >
          <Checkbox
            v-model="recurring_submit_copy"
            data-test="recurring_submit_copy"
            id="recurring_submit_copy"
          >
            {{ $t('form.invoiceForm.fields.recurring.submitCopy.label') }}
          </Checkbox>
        </FormGroup>
      </Column>
    </Row>
  </FormSection>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapGetters, mapState } from 'vuex';
import {
  addWeeks, addMonths, addQuarters, addYears, endOfMonth,
} from 'date-fns';

export default {
  name: 'RecurringSettings',
  data: () => ({
    featureEnabled: false,
    frequency: [
      'weekly',
      'fortnightly',
      'monthly',
      'quarterly',
      'halfyearly',
      'yearly',
    ],
    whenEnd: [
      'never',
      'after_count',
      'after_date',
    ],
  }),
  computed: {
    ...mapGetters('auth', ['isAuthorized', 'userHasPremiumPlan']),
    ...mapGetters('env', ['dateFormats']),
    ...mapFields('invoiceForm', ['id', 'recurring_enabled', 'recurring_end_options',
      'recurring_frequency', 'recurring_ends_on', 'recurring_ends_after_count',
      'recurring_issue_last_day', 'recurring_auto_submit', 'recurring_submit_copy',
      'issued_on', 'tax_point_on']),
    ...mapState('invoiceForm', ['recurring_draft', 'recurring_profile']),
    frequencyOptions() {
      return this.frequency.map(f => ({
        key: f,
        value: this.$i18n.t(`form.invoiceForm.fields.recurring.frequency.options.${f}`),
      }));
    },
    whenEndOptions() {
      return this.whenEnd.map(f => ({
        key: f,
        value: this.$i18n.t(`form.invoiceForm.fields.recurring.endOptions.options.${f}`),
      }));
    },
    showCountInput() {
      return this.recurring_end_options === 'after_count';
    },
    showDateInput() {
      return this.recurring_end_options === 'after_date';
    },
    nextInvoiceDate() {
      const currentDate = this.issued_on;
      let nextDate;

      switch (this.recurring_frequency) {
        case 'weekly':
          nextDate = addWeeks(currentDate, 1);
          break;
        case 'fortnightly':
          nextDate = addWeeks(currentDate, 2);
          break;
        case 'monthly':
          nextDate = addMonths(currentDate, 1);
          break;
        case 'quarterly':
          nextDate = addQuarters(currentDate, 1);
          break;
        case 'halfyearly':
          nextDate = addMonths(currentDate, 6);
          break;
        case 'yearly':
          nextDate = addYears(currentDate, 1);
          break;
        default:
          nextDate = currentDate;
      }

      return this.recurring_issue_last_day ? endOfMonth(nextDate) : nextDate;
    },
    canSetRecurring() {
      return this.recurring_draft || !this.recurring_profile;
    },
    recurringEnabled: {
      get() {
        return this.recurring_profile || this.recurring_enabled;
      },
      set(newValue) {
        this.recurring_enabled = newValue;
      },
    },
    canSetRecurringLastDayInMonth() {
      if (this.recurring_frequency === 'weekly') {
        return false;
      }
      return true;
    },
    canSetCopyTomyEmail() {
      return this.recurring_auto_submit;
    },
    pickerOptions() {
      const nextInvoiceDate = this.nextInvoiceDate || new Date();

      return {
        firstDayOfWeek: 1,
        disabledDate(time) {
          return time.getTime() <= nextInvoiceDate.getTime();
        },
      };
    },
    firstAvailableDate() {
      return this.nextInvoiceDate;
    },
  },
  methods: {
    showUpgradeDialog() {
      if (!this.userHasPremiumPlan) {
        this.$store.dispatch('invoiceForm/openPremiumDialog', 'recurring');
      }
    },
    handleDateSelected() {
      this.tax_point_on = this.issued_on;
    },
    handleChangeDateAnchorClick() {
      this.$refs.datepicker.$refs.reference = this.$refs.anchor.$el;
      this.$refs.datepicker.showPicker();
    },
    decideFeature() {
      const { recurring_enabled: featureEnabled = false } = this.$route.query;

      if (featureEnabled === 'true') {
        this.featureEnabled = true;
      }

      this.$posthog?.onFeatureFlags(() => {
        if (this.$posthog?.isFeatureEnabled('recurring-invoices-enabled')) {
          this.featureEnabled = true;
        }
      });
    },
  },
  watch: {
    recurring_frequency(newVal) {
      if (newVal === 'weekly') {
        this.recurring_issue_last_day = false;
      }
    },
    recurring_auto_submit(newVal) {
      if (newVal === false) {
        this.recurring_submit_copy = false;
      }
    },
    recurring_end_options(newVal) {
      const optionsToResetFields = {
        never: ['recurring_ends_on', 'recurring_ends_after_count'],
        after_count: ['recurring_ends_on'],
        after_date: ['recurring_ends_after_count'],
      };

      const fieldsToReset = optionsToResetFields[newVal] || [];

      fieldsToReset.forEach((field) => {
        this[field] = null;
      });
    },
  },
  mounted() {
    this.decideFeature();
  },
};
</script>

<style lang="scss" scoped>
.datepicker-helper {
  display: none;
}

.recurring-header {
  margin-bottom: 22px;
  row-gap: 1rem;
}

.recurring-dates {
  display: flex;
  justify-content: flex-end;
  gap: 1rem 2rem;
  flex-wrap: nowrap;

  @media only screen and (max-width: ($--md - 1px)) {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

.recurring-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.recurring-label {
  font-size: rem(13px);
  position: relative;
  top: -4px;
  left: -3px;

  .icon {
    color: $--color-primary;
    font-size: rem(18px);
    position: relative;
    top: 4px;
  }
}

.recurring-checkboxes {
  @media only screen and (max-width: ($--md - 1px)) {
    margin-top: 1rem;
  }
}
</style>
